/**
 * The mobile navigation drawer is a daisyUI checkbox drawer: its open state lives in
 * `#app-drawer`, and every control (the topbar button, the dock's "More", the overlay) is a
 * <label> pointing at it, so each one *toggles* rather than sets.
 *
 * Opening and closing is done entirely with CSS transitions on opacity, visibility and
 * translate. That matters, because WebKit pauses running transitions when a page is hidden
 * and does not reliably resume them on return. A shortcut opens an application host in
 * another window, which hides the page; if a drawer transition is in flight at that moment
 * it freezes, and the app comes back with the checkbox saying "closed" while the pixels
 * still say "open" -- a dimmed overlay covering the screen. The next tap then resolves the
 * frozen transition the wrong way: the menu flashes and settles invisible, though it is
 * still laid out and still takes taps.
 *
 * So the drawer is never animated across a visibility change. It is closed instantly on the
 * way out, and the painted state is re-synced on the way back in.
 */

export const DRAWER_ID = 'app-drawer';
/** While set on <html>, drawer transitions are suppressed (see index.css). */
const INSTANT_ATTR = 'data-drawer-instant';

export function drawerInput(): HTMLInputElement | null {
  if (typeof document === 'undefined') return null;
  const el = document.getElementById(DRAWER_ID);
  return el instanceof HTMLInputElement ? el : null;
}

export const isDrawerOpen = (): boolean => drawerInput()?.checked ?? false;

/** Closes the drawer, animating as usual. For in-page use, such as following a link. */
export function closeDrawer(): void {
  const input = drawerInput();
  if (input?.checked) input.checked = false;
}

/**
 * Closes the drawer with no animation, and forces the browser to settle on the closed
 * styles before returning. Nothing is left in flight to freeze.
 *
 * Also repairs the reverse case: if a previous transition was already frozen, recomputing
 * style with transitions suppressed snaps the paint back into agreement with the checkbox.
 */
export function closeDrawerInstantly(): void {
  const input = drawerInput();
  if (!input) return;
  const root = document.documentElement;
  root.setAttribute(INSTANT_ATTR, '');
  input.checked = false;
  // Read back a layout value to force a synchronous style recalculation while transitions
  // are still suppressed, so the closed state is applied outright rather than animated to.
  document.querySelector('.drawer-side')?.getBoundingClientRect();
  root.removeAttribute(INSTANT_ATTR);
}

/**
 * Keeps the drawer honest across a round trip out of the app. Returns a cleanup function.
 */
export function watchDrawerAcrossVisibility(): () => void {
  if (typeof window === 'undefined') return () => {};
  const onVisibility = () => {
    // Both directions: closing on the way out avoids freezing a transition, and repeating
    // it on the way back repairs one that was frozen before this code ran.
    closeDrawerInstantly();
  };
  window.addEventListener('pagehide', onVisibility);
  window.addEventListener('pageshow', onVisibility);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('pagehide', onVisibility);
    window.removeEventListener('pageshow', onVisibility);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
